import React from 'react';

const PAIN_POINTS = [
  {
    before: 'Staff manually entering orders, invoices, or customer data every day',
    after: 'Automated data capture that runs 24/7 without human error',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
  {
    before: 'No follow-up system — leads and customers fall through the cracks',
    after: 'Automated reminders, follow-ups, and re-engagement that never miss a beat',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
  },
  {
    before: 'Reporting done manually in spreadsheets — hours wasted every week',
    after: 'Live dashboards with real-time data, no spreadsheet needed',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    before: 'Scheduling, bookings, or approvals managed through texts and emails',
    after: 'Automated workflows that handle requests, approvals, and notifications instantly',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
];

const INDUSTRIES = [
  { name: 'Restaurants & Hospitality', icon: '🍽️', example: 'Customer CRM, reservation follow-ups, supplier ordering' },
  { name: 'Real Estate', icon: '🏠', example: 'Lead tracking, contract management, client notifications' },
  { name: 'Healthcare & Clinics', icon: '🏥', example: 'Appointment reminders, patient intake, billing automation' },
  { name: 'Retail & E-commerce', icon: '🛍️', example: 'Inventory alerts, order tracking, customer loyalty' },
  { name: 'Logistics & Distribution', icon: '🚚', example: 'Shipment updates, route optimization, supplier alerts' },
  { name: 'Professional Services', icon: '💼', example: 'Client onboarding, invoice generation, project tracking' },
];

const Automation: React.FC = () => {
  return (
    <section id="automation" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section header */}
        <div className="max-w-2xl mb-16">
          <p className="text-blue-700 text-sm font-semibold uppercase tracking-widest mb-3">
            Business Automation
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
            Is Your Team Doing Work a System Should Handle?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Most businesses are losing money every week to manual processes that could be fully automated.
            We identify those bottlenecks and build the software to eliminate them — for good.
          </p>
        </div>

        {/* Before / After grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-20">
          {PAIN_POINTS.map((item, i) => (
            <div key={i} className="rounded-2xl border border-gray-200 overflow-hidden">
              {/* Before */}
              <div className="flex items-start gap-4 p-5 bg-red-50 border-b border-red-100">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center text-red-500 shrink-0 mt-0.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold text-red-400 uppercase tracking-widest mb-1">Before</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{item.before}</p>
                </div>
              </div>
              {/* After */}
              <div className="flex items-start gap-4 p-5 bg-green-50">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-green-600 shrink-0 mt-0.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold text-green-600 uppercase tracking-widest mb-1">After CloudIgnite</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{item.after}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Industries */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">We Automate Across Industries</h3>
          <p className="text-gray-500 text-center mb-10">If your business has a repetitive process, we can build a system to handle it.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INDUSTRIES.map((industry) => (
              <div
                key={industry.name}
                className="flex items-start gap-4 p-5 rounded-2xl border border-gray-200 hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-200"
              >
                <span className="text-2xl">{industry.icon}</span>
                <div>
                  <p className="font-semibold text-gray-900 text-sm mb-1">{industry.name}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{industry.example}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How it works strip */}
        <div className="bg-slate-900 rounded-2xl p-10 text-white">
          <h3 className="text-xl font-bold mb-8 text-center">How We Work</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'We map out your current processes and identify exactly where you\'re losing time and money.' },
              { step: '02', title: 'Build', desc: 'We design and develop a custom solution — no off-the-shelf software, no workarounds.' },
              { step: '03', title: 'Deploy & Support', desc: 'We deploy it to the cloud, train your team, and stay on hand as your business grows.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm mx-auto mb-4">
                  {s.step}
                </div>
                <h4 className="font-semibold text-white mb-2">{s.title}</h4>
                <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a
              href="#contact"
              className="inline-flex items-center bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3 rounded-lg transition-colors duration-200"
            >
              Book a Free Automation Audit
            </a>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Automation;
